'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  ChevronDown,
  ChevronRight,
  Clock3,
  Loader2,
  RefreshCw,
  Search,
  Shield,
  Sparkles,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import masterSync from '@/lib/masterSync';
import { formatDateSAST, formatDateTimeSAST } from '@/lib/sastTime';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { useTheme } from '@/contexts/ThemeContext';
import { OPERATIONS_SYNC_PENDING_CHANGED_EVENT } from '@/lib/operations/offlineSyncEvents';
import {
  type SmartTaskOfflineActionHistoryEntry,
  type SmartTaskQueuedAction,
} from '@/lib/operations/smartTaskOfflineActions';
import { WORK_ITEM_LIST_CHANGED_EVENT } from '@/lib/websocket/lifecycle';
import { UserAvatar, UserIdentityLink } from './WorkItemUserIdentity';
import { parseStakeholderEntityWorkItemId } from '@/lib/contacts/stakeholderWorkItemIdentity';
import { isValidCoord } from '@/lib/mapConstants';
// Leaflet CSS is required for ViolationMap (rendered inside location accordions) to
// display correctly. ViolationMapInner does not import the CSS itself — it relies on
// it being loaded by sibling components in the total-attendance module. When
// ActivityTimeline is used outside that module (e.g. WorkItemDetailModal), the CSS
// may never be loaded, causing the map to collapse to a tiny square.
import 'leaflet/dist/leaflet.css';
// Reused verbatim from total-attendance so activity locations render identically to violations.
// ActivityTimeline is a 'use client' module and ViolationMap already dynamic-imports its
// leaflet inner component with ssr:false, so this import is SSR-safe.
import ViolationMap from '@/components/attendance/components/ViolationMap';

interface ActivityChange {
  field: string;
  old_value: unknown;
  new_value: unknown;
  source?: 'activity_log' | 'field_history' | 'workflow_transition';
  reference_type?: 'user' | 'contact' | 'contact_person' | 'location' | 'work_item' | null;
  old_display_value?: string | null;
  new_display_value?: string | null;
}

interface ActivityUser {
  id?: number;
  email?: string;
  name?: string;
}

interface ActivityEntry {
  id: string;
  action: string;
  source?: 'activity_log' | 'field_history' | 'workflow_transition' | 'offline_queue' | 'offline_history';
  created_at: string;
  user?: ActivityUser;
  field_changed?: string | null;
  old_value?: unknown;
  new_value?: unknown;
  metadata?: Record<string, unknown> | null;
  changes?: ActivityChange[];
  tags?: string[];
}

interface ActivityTimelineProps {
  workItemId: string;
  isActive: boolean;
  includeRelated?: boolean;
  groupId?: string | null;
  onActivityCountChange?: (count: number) => void;
}

interface CurrentUserSnapshot {
  id?: number | string | null;
  email?: string | null;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  client_id?: number | string | null;
}

interface RealtimeMessage {
  type?: unknown;
  group_id?: unknown;
  work_item_id?: unknown;
  parent_job_id?: unknown;
  appointment_id?: unknown;
  appointment_ids?: unknown;
}

interface PrettyValueRow {
  key: string;
  label: string;
  value: string;
}

interface ComparisonRow {
  key: string;
  label: string;
  previous: string;
  next: string;
  changed: boolean;
}

const ITEMS_PER_PAGE = 50;

const OFFLINE_ACTION_LABELS: Record<string, string> = {
  start_travel: 'Start travel',
  arrive_site: 'Arrive on site',
  check_in: 'Check in',
  check_in_without_travel: 'Check in',
  pause_work: 'Pause work',
  resume_work: 'Resume work',
  start_completing: 'Start completing',
  complete_job: 'Complete appointment',
  route_stop_update: 'Route stop update',
};

const FRIENDLY_FIELD_LABELS: Record<string, string> = {
  workflow_state: 'Workflow state',
  chat_thread_created_at: 'Chat thread created',
  signature_captured_at: 'Signature captured',
  last_activity_at: 'Last activity recorded',
  comment_text: 'Comment',
  comment_created_at: 'Comment date & time',
  is_internal: 'Internal note',
  notification_time: 'Notification time',
  equipment_required: 'Equipment required',
  safety_requirements: 'Safety requirements',
  schedule_immediately: 'Schedule immediately',
  geofence_radius_meters: 'Geofence radius (m)',
  require_exact_location: 'Require exact location',
  scheduled_start: 'Scheduled start',
  scheduled_end: 'Scheduled end',
  arrived_at: 'Arrival time',
  travel_started_at: 'Travel start',
  started_at: 'Timer start',
  ended_at: 'Timer end',
  duration_hours: 'Duration',
  duration_minutes: 'Duration (minutes)',
  is_billable: 'Billable',
  check_in_timestamp: 'Check-in time',
  check_out_timestamp: 'Check-out time',
  photo_url: 'Photo',
  photo: 'Photo',
  photos: 'Photos',
  'completed_travel_entry.distance_km': 'Travel distance',
};

const EVENT_FIELD_SUMMARIES: Record<string, string> = {
  chat_thread_created_at: 'Chat thread created',
  signature_captured_at: 'Signature captured',
  last_activity_at: 'Activity recorded',
  scheduled_start: 'Schedule updated',
  scheduled_end: 'Schedule updated',
  arrived_at: 'Arrival recorded',
  travel_started_at: 'Travel started',
  check_in_timestamp: 'Check-in recorded',
  check_out_timestamp: 'Check-out recorded',
};

const LOW_VALUE_FIELDS = new Set([
  'last_activity_at',
]);

// Actions that are pure UI noise — component mounts, list refreshes, etc.
// These flood the timeline (60%+ of entries) with zero business value for end clients.
// Hidden by default; users can toggle them on with "Show all activity".
const NOISE_ACTIONS = new Set([
  'open',                    // "WorkItemDetailModal: open" — just opening the detail view
  'work_item_visible',       // "refreshed SmartTask queue" — duplicated 5x in 2 min
  'work_item_opened',        // "opened APT2426 in SmartTask" — low-value view events
]);

// Actions that are backend system events — never shown to end clients.
// These are internal optimizations (e.g. photo format conversion) with no user-facing meaning.
const SYSTEM_ONLY_ACTIONS = new Set([
  'photo_converted_to_webp', // Backend image optimization — not a user action
]);

// Window for aggregating consecutive identical entries from the same user (in milliseconds).
// E.g. "refreshed SmartTask queue" 5 times in 2 minutes → 1 aggregated entry with ×5 badge.
const TIMELINE_AGGREGATION_WINDOW_MS = 2 * 60 * 1000; // 2 minutes

// Check if a URL string points to an image (for photo change display).
const isImageUrl = (value: string): boolean =>
  /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(value) ||
  /\/files\/\d+\/drives\//i.test(value);

// Extract a display-friendly label from a cloud storage URL.
// E.g. ".../2026/08/85d8c7f1...jpg" → "Photo (Aug 2026)"
const getPhotoUrlLabel = (url: string): string => {
  const dateMatch = url.match(/\/(\d{4})\/(\d{2})\//);
  if (dateMatch) {
    const [, year, month] = dateMatch;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIdx = Math.max(0, Math.min(11, parseInt(month, 10) - 1));
    const ext = url.match(/\.(\w+)$/)?.[1]?.toUpperCase() || 'photo';
    return `${ext} photo (${monthNames[monthIdx]} ${year})`;
  }
  return 'Photo';
};

// Per-assignee SmartTask workflow states (field_worker_workflow_transitions).
// Only field workers ever have workflow_transition records, so any entry carrying
// a GPS location is by definition a field-worker event — non-field workers (e.g.
// high management) never produce these transitions and therefore never have their
// movements plotted here.
const WORKFLOW_STATE_LABELS: Record<string, string> = {
  idle: 'Idle',
  ready: 'Ready',
  traveling: 'On route',
  travel_paused: 'Travel paused',
  arrived: 'On site',
  checked_in: 'Checked in',
  working: 'Working',
  in_progress: 'In progress',
  paused: 'Paused',
  completing: 'Completing',
  inspection_pending: 'Inspection pending',
  vehicle_required: 'Vehicle required',
};

const workflowStateLabel = (state?: unknown): string => {
  const normalized = String(state ?? '').trim().toLowerCase();
  if (!normalized) return '';
  return WORKFLOW_STATE_LABELS[normalized] || String(state);
};

interface ActivityLocation {
  lat: number;
  lng: number;
  accuracy?: number | null;
}

const getLocationFromEntry = (entry: ActivityEntry): ActivityLocation | null => {
  const loc = entry.metadata?.location;
  if (!loc || typeof loc !== 'object') return null;
  const lat = Number((loc as Record<string, unknown>).lat);
  const lng = Number((loc as Record<string, unknown>).lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const rawAccuracy = (loc as Record<string, unknown>).accuracy;
  const accuracy = rawAccuracy == null ? null : Number(rawAccuracy);
  return { lat, lng, accuracy: Number.isFinite(accuracy as number) ? (accuracy as number) : null };
};

const getAuthToken = (): string => authService.getTokens()?.access_token || '';

const toTitleCase = (value: string): string =>
  value
    .replace(/^custom_fields\./, '')
    .replace(/[_\.]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isValueEmpty = (value: unknown): boolean =>
  value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

const isDateLikeString = (value: string): boolean => {
  if (!/\d{4}-\d{2}-\d{2}/.test(value)) return false;
  return !Number.isNaN(new Date(value).getTime());
};

const getFriendlyFieldLabel = (field?: string | null): string => {
  const normalized = (field || '').replace(/^custom_fields\./, '').trim();
  if (!normalized) return 'Details';
  return FRIENDLY_FIELD_LABELS[normalized] || toTitleCase(normalized);
};

const isLowValueField = (field?: string | null): boolean => {
  const normalized = (field || '').replace(/^custom_fields\./, '').trim();
  return LOW_VALUE_FIELDS.has(normalized);
};

const toSentenceLabel = (value: string): string =>
  value.length > 0 ? value.charAt(0).toLowerCase() + value.slice(1) : value;

const formatList = (items: string[]): string => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

function formatDateTime(dateString: string): string {
  return formatDateTimeSAST(dateString, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatFieldName(field?: string | null): string {
  if (!field) return 'Details';
  if (field === 'custom_fields') return 'Custom details';
  return getFriendlyFieldLabel(field);
}

const formatSimpleValue = (value: unknown): string => {
  if (isValueEmpty(value)) return 'Not set';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : 'Not set';
  if (typeof value === 'string') {
    // Cloud storage photo URLs — show a friendly label instead of a long opaque URL
    if (isImageUrl(value) && value.length > 80) {
      return getPhotoUrlLabel(value);
    }
    return isDateLikeString(value) ? formatDateTime(value) : value;
  }
  if (Array.isArray(value)) {
    if (value.every((item) => !isPlainObject(item) && !Array.isArray(item))) {
      return value.map((item) => formatSimpleValue(item)).join(', ');
    }
    return `${value.length} item${value.length === 1 ? '' : 's'}`;
  }
  if (isPlainObject(value)) {
    if ('lat' in value && 'lng' in value) {
      return `${value.lat}, ${value.lng}`;
    }
    if ('x' in value && 'y' in value) {
      return `${value.y}, ${value.x}`;
    }
    const entryCount = Object.keys(value).length;
    return `${entryCount} detail${entryCount === 1 ? '' : 's'}`;
  }
  return String(value);
};

const getDisplayOverride = (value?: string | null): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const getChangePreviousValue = (change: ActivityChange): string =>
  getDisplayOverride(change.old_display_value) || formatSimpleValue(change.old_value);

const getChangeNextValue = (change: ActivityChange): string =>
  getDisplayOverride(change.new_display_value) || formatSimpleValue(change.new_value);

const hasResolvedDisplayValue = (change: ActivityChange): boolean =>
  Boolean(getDisplayOverride(change.old_display_value) || getDisplayOverride(change.new_display_value));

const toSentenceCase = (value: string): string =>
  value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const flattenValueRows = (value: unknown, path = '', depth = 0): PrettyValueRow[] => {
  if (isValueEmpty(value)) return [];

  if (Array.isArray(value)) {
    if (value.every((item) => !isPlainObject(item) && !Array.isArray(item))) {
      return [{
        key: path || 'value',
        label: getFriendlyFieldLabel(path || 'value'),
        value: value.map((item) => formatSimpleValue(item)).join(', '),
      }];
    }

    if (depth >= 1) {
      return [{
        key: path || 'value',
        label: getFriendlyFieldLabel(path || 'value'),
        value: `${value.length} item${value.length === 1 ? '' : 's'}`,
      }];
    }

    return value.flatMap((item, index) => flattenValueRows(item, `${path}${path ? '.' : ''}${index + 1}`, depth + 1));
  }

  if (isPlainObject(value)) {
    if ('lat' in value && 'lng' in value) {
      return [{
        key: path || 'coordinates',
        label: getFriendlyFieldLabel(path || 'coordinates'),
        value: `${value.lat}, ${value.lng}`,
      }];
    }

    if ('x' in value && 'y' in value) {
      return [{
        key: path || 'coordinates',
        label: getFriendlyFieldLabel(path || 'coordinates'),
        value: `${value.y}, ${value.x}`,
      }];
    }

    const entries = Object.entries(value);
    if (entries.length === 0) return [];

    return entries.flatMap(([key, nestedValue]) => {
      const nestedPath = path ? `${path}.${key}` : key;

      if (isPlainObject(nestedValue) || Array.isArray(nestedValue)) {
        if (depth >= 1) {
          return [{
            key: nestedPath,
            label: getFriendlyFieldLabel(nestedPath),
            value: formatSimpleValue(nestedValue),
          }];
        }

        return flattenValueRows(nestedValue, nestedPath, depth + 1);
      }

      return [{
        key: nestedPath,
        label: getFriendlyFieldLabel(nestedPath),
        value: formatSimpleValue(nestedValue),
      }];
    });
  }

  return [{
    key: path || 'value',
    label: getFriendlyFieldLabel(path || 'value'),
    value: formatSimpleValue(value),
  }];
};

const buildComparisonRows = (change: ActivityChange): ComparisonRow[] => {
  const baseField = change.field || 'value';
  const previousDisplayValue = getChangePreviousValue(change);
  const nextDisplayValue = getChangeNextValue(change);

  if (hasResolvedDisplayValue(change)) {
    return [{
      key: baseField,
      label: formatFieldName(change.field),
      previous: previousDisplayValue,
      next: nextDisplayValue,
      changed: previousDisplayValue !== nextDisplayValue,
    }];
  }

  const oldRows = flattenValueRows(change.old_value, baseField);
  const newRows = flattenValueRows(change.new_value, baseField);

  if (oldRows.length === 0 && newRows.length === 0) {
    return [{
      key: baseField,
      label: formatFieldName(change.field),
      previous: formatSimpleValue(change.old_value),
      next: formatSimpleValue(change.new_value),
      changed: formatSimpleValue(change.old_value) !== formatSimpleValue(change.new_value),
    }];
  }

  const oldMap = new Map(oldRows.map((row) => [row.key, row]));
  const newMap = new Map(newRows.map((row) => [row.key, row]));
  const keys = Array.from(new Set([...oldMap.keys(), ...newMap.keys()]));

  return keys.map((key) => {
    const oldRow = oldMap.get(key);
    const newRow = newMap.get(key);
    const previous = oldRow?.value || 'Not set';
    const next = newRow?.value || 'Not set';

    return {
      key,
      label: newRow?.label || oldRow?.label || formatFieldName(change.field),
      previous,
      next,
      changed: previous !== next,
    };
  });
};

const getDisplayChanges = (changes?: ActivityChange[]): ActivityChange[] => {
  if (!Array.isArray(changes)) return [];

  return changes.filter((change, _index, allChanges) => {
    const field = change.field?.trim();
    if (!field) return true;
    if (isLowValueField(field)) return false;

    return !allChanges.some((otherChange) => otherChange !== change && otherChange.field?.startsWith(`${field}.`));
  });
};

const getOfflineTimelineLabel = (action?: string | null, timelineLabel?: string | null): string => {
  if (typeof timelineLabel === 'string' && timelineLabel.trim().length > 0) {
    return timelineLabel.trim();
  }

  if (action && OFFLINE_ACTION_LABELS[action]) {
    return OFFLINE_ACTION_LABELS[action];
  }

  return 'Smart Task action';
};

const matchesOfflineScope = (
  entry: SmartTaskQueuedAction | SmartTaskOfflineActionHistoryEntry,
  scope: { workItemId: string; includeRelated: boolean; groupId?: string | null },
): boolean => {
  const entryWorkItemId = entry.work_item_id ? String(entry.work_item_id) : null;
  const groupId = typeof entry.activity_context?.group_id === 'string' ? entry.activity_context.group_id : null;
  const parentJobId = entry.activity_context?.parent_job_id ? String(entry.activity_context.parent_job_id) : null;

  if (scope.groupId && groupId === scope.groupId) {
    return true;
  }

  if (entryWorkItemId === scope.workItemId) {
    return true;
  }

  if (scope.includeRelated && parentJobId === scope.workItemId) {
    return true;
  }

  return false;
};

const buildOfflineActivityEntry = (
  entry: SmartTaskQueuedAction | SmartTaskOfflineActionHistoryEntry,
  status: 'queued' | 'failed',
  currentUser: ActivityUser,
): ActivityEntry => ({
  id: `offline-${status}-${entry.local_action_id}`,
  source: status === 'queued' ? 'offline_queue' : 'offline_history',
  action: status === 'queued' ? 'offline_queued' : 'offline_failed',
  created_at: entry.created_at,
  user: currentUser,
  metadata: {
    offline_status: status,
    offline_action: entry.action,
    timeline_label: getOfflineTimelineLabel(entry.action, entry.timeline_label),
    offline_error: 'error' in entry ? entry.error || null : null,
    source_work_item_id: entry.work_item_id || null,
    source_work_item_number: entry.activity_context?.work_item_number || null,
    source_work_item_type: entry.activity_context?.item_type || null,
    group_id: entry.activity_context?.group_id || null,
    group_number: entry.activity_context?.group_number || null,
    parent_job_id: entry.activity_context?.parent_job_id || null,
  },
});

const getTabLabelForField = (field?: string | null): string => {
  const normalized = (field || '').trim();
  if (!normalized) return 'Activity';

  if (normalized === 'comment_text' || normalized === 'comment_created_at' || normalized === 'is_internal') return 'Comments';
  if (normalized.startsWith('signature') || normalized.startsWith('has_signature')) return 'Signatures';
  if (normalized.startsWith('photo') || normalized.startsWith('supporting_photo')) return 'Photos';
  if (normalized.includes('attachment')) return 'Attachments';
  if (normalized.includes('watcher')) return 'Watchers';
  if (
    normalized.includes('hour') ||
    normalized.includes('duration') ||
    normalized.includes('started_at') ||
    normalized.includes('ended_at') ||
    normalized.includes('billable') ||
    normalized.includes('hourly_rate')
  ) {
    return 'Time Tracking';
  }
  if (
    normalized.includes('cost') ||
    normalized.includes('amount') ||
    normalized.includes('subtotal') ||
    normalized.includes('tax') ||
    normalized.includes('grand_total') ||
    normalized.includes('payment')
  ) {
    return 'Billing';
  }
  if (
    normalized.startsWith('location') ||
    normalized.startsWith('contact') ||
    normalized.startsWith('reporter') ||
    normalized.startsWith('scheduled_') ||
    normalized.startsWith('start_') ||
    normalized.startsWith('due_') ||
    normalized.startsWith('custom_fields.') ||
    normalized === 'title' ||
    normalized === 'description' ||
    normalized === 'status' ||
    normalized === 'priority' ||
    normalized === 'severity' ||
    normalized === 'assigned_to' ||
    normalized === 'assigned_team' ||
    normalized === 'item_subtype' ||
    normalized === 'progress_percentage' ||
    normalized === 'tags' ||
    normalized === 'labels'
  ) {
    return 'Overview';
  }

  return 'Details';
};

const getActivityAccent = (entry: ActivityEntry, isDark: boolean) => {
  if (entry.action === 'offline_failed') {
    return isDark
      ? 'border-l-red-500 bg-red-950/15'
      : 'border-l-red-500 bg-red-50/80';
  }

  if (entry.action === 'offline_queued') {
    return isDark
      ? 'border-l-amber-500 bg-amber-950/15'
      : 'border-l-amber-500 bg-amber-50/80';
  }

  if (entry.action.includes('comment')) {
    return isDark
      ? 'border-l-cyan-500 bg-cyan-950/10'
      : 'border-l-cyan-500 bg-cyan-50/80';
  }

  if (
    entry.action === 'field_updated' ||
    entry.action === 'updated' ||
    entry.action === 'assigned' ||
    entry.action === 'unassigned' ||
    entry.action === 'reassigned' ||
    entry.action === 'status_changed'
  ) {
    return isDark
      ? 'border-l-blue-500 bg-blue-950/10'
      : 'border-l-blue-500 bg-blue-50/80';
  }

  if (entry.action === 'created') {
    return isDark
      ? 'border-l-emerald-500 bg-emerald-950/10'
      : 'border-l-emerald-500 bg-emerald-50/80';
  }

  if (entry.action === 'deleted') {
    return isDark
      ? 'border-l-red-500 bg-red-950/10'
      : 'border-l-red-500 bg-red-50/80';
  }

  return isDark
    ? 'border-l-gray-600 bg-gray-900/70'
    : 'border-l-gray-300 bg-white';
};

const getSourceWorkItemLabel = (entry: ActivityEntry): string | null => {
  const sourceNumber = typeof entry.metadata?.source_work_item_number === 'string'
    ? entry.metadata.source_work_item_number.trim()
    : '';

  if (sourceNumber) {
    return sourceNumber;
  }

  const sourceType = typeof entry.metadata?.source_work_item_type === 'string'
    ? entry.metadata.source_work_item_type.trim()
    : '';

  return sourceType ? toTitleCase(sourceType) : null;
};

const getEntryItemReference = (entry: ActivityEntry): string | null => {
  const itemNumber = typeof entry.metadata?.item_number === 'string'
    ? entry.metadata.item_number.trim()
    : '';

  return itemNumber || getSourceWorkItemLabel(entry);
};

const humanizeTechnicalPhrase = (value: string): string => value
  .replace(/SmartTaskShell/gi, 'SmartTask workspace')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/[_:.]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\bOauth\b/g, 'OAuth')
  .replace(/\bOtp\b/g, 'OTP')
  .replace(/\bMfa\b/g, 'MFA')
  .replace(/\bApi\b/g, 'API')
  .replace(/\bUi\b/g, 'in-app');

const getSmartTaskComponentLabel = (value?: string | null): string => {
  switch ((value || '').trim().toLowerCase()) {
    case 'smarttaskshell':
    case 'smarttask':
      return 'SmartTask workspace';
    default:
      return value ? humanizeTechnicalPhrase(value) : 'the application';
  }
};

const getAuditActionPhrase = (value?: string | null): string | null => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) {
    return null;
  }

  if (/^smarttaskshell\s*:\s*mount$/i.test(trimmed)) {
    return 'opened SmartTask';
  }

  if (/^smarttaskshell\s*:\s*unmount$/i.test(trimmed)) {
    return 'closed SmartTask';
  }

  if (/^(open|opened)$/i.test(trimmed)) {
    return 'opened this record';
  }

  if (/^(view|viewed)$/i.test(trimmed)) {
    return 'viewed this record';
  }

  if (/^ui action$/i.test(trimmed)) {
    return 'recorded in-app activity';
  }

  const recordedDetailsMatch = trimmed.match(/^recorded\s+record\s+as\s+(.+)$/i);
  if (recordedDetailsMatch) {
    return `recorded ${recordedDetailsMatch[1]} for the record`;
  }

  const humanized = humanizeTechnicalPhrase(trimmed);
  return humanized ? toSentenceLabel(humanized) : null;
};

const getUiActionSummary = (entry: ActivityEntry): string => {
  const metadata = entry.metadata || {};
  const actionDetails = getAuditActionPhrase(typeof metadata.action_details === 'string' ? metadata.action_details : null);
  if (actionDetails) {
    return actionDetails;
  }

  const itemReference = getEntryItemReference(entry);
  const uiAction = typeof metadata.ui_action === 'string' ? metadata.ui_action.trim() : '';
  if (uiAction === 'work_item_visible') {
    const reason = typeof metadata.visibility_reason === 'string' ? metadata.visibility_reason.trim() : '';
    const ref = itemReference || 'this record';
    if (reason === 'list_refresh') {
      return `refreshed their SmartTask queue — ${ref} surfaced`;
    }
    if (reason) {
      return `${ref} surfaced in SmartTask (${reason.replace(/[_-]+/g, ' ')})`;
    }
    return `made ${ref} available in SmartTask`;
  }

  if (uiAction === 'work_item_opened') {
    return itemReference
      ? `opened ${itemReference} in SmartTask`
      : 'opened this record in SmartTask';
  }

  const uiComponent = typeof metadata.ui_component === 'string'
    ? metadata.ui_component.trim()
    : typeof metadata.component === 'string'
      ? metadata.component.trim()
      : '';

  if (uiComponent) {
    return `used ${getSmartTaskComponentLabel(uiComponent)}`;
  }

  return 'recorded in-app activity';
};

const withSourceContext = (summary: string, entry: ActivityEntry, includeSource: boolean): string => {
  if (!includeSource) {
    return summary;
  }

  const sourceLabel = getSourceWorkItemLabel(entry);
  return sourceLabel ? `${summary} on ${sourceLabel}` : summary;
};

const getChangeSummary = (change: ActivityChange): string => {
  const normalizedField = change.field?.replace(/^custom_fields\./, '') || '';
  const label = toSentenceLabel(formatFieldName(change.field));
  const previous = getChangePreviousValue(change);
  const next = getChangeNextValue(change);
  const eventSummary = EVENT_FIELD_SUMMARIES[normalizedField];

  if (normalizedField === 'workflow_state') {
    if (isValueEmpty(change.old_value) && !isValueEmpty(change.new_value)) {
      return `moved the workflow to ${next}`;
    }
    if (!isValueEmpty(change.old_value) && isValueEmpty(change.new_value)) {
      return 'cleared the workflow state';
    }
    return `moved the workflow from ${previous} to ${next}`;
  }

  if (eventSummary) {
    return next !== 'Not set'
      ? `${toSentenceLabel(eventSummary)} at ${next}`
      : toSentenceLabel(eventSummary);
  }

  if (normalizedField === 'assigned_to' || normalizedField === 'assigned_user_id') {
    if (isValueEmpty(change.old_value) && !isValueEmpty(change.new_value)) {
      return `assigned ${next}`;
    }
    if (!isValueEmpty(change.old_value) && isValueEmpty(change.new_value)) {
      return 'removed the assignee';
    }
    return `reassigned from ${previous} to ${next}`;
  }

  if (normalizedField === 'status') {
    if (isValueEmpty(change.old_value) && !isValueEmpty(change.new_value)) {
      return `set the status to ${next}`;
    }
    if (!isValueEmpty(change.old_value) && isValueEmpty(change.new_value)) {
      return 'cleared the status';
    }
    return `changed the status from ${previous} to ${next}`;
  }

  // Photo URL fields — show a friendly summary instead of raw cloud storage URLs
  if (normalizedField === 'photo_url' || normalizedField === 'photo') {
    if (isValueEmpty(change.old_value) && !isValueEmpty(change.new_value)) {
      return 'added a photo';
    }
    if (!isValueEmpty(change.old_value) && isValueEmpty(change.new_value)) {
      return 'removed a photo';
    }
    return 'updated a photo';
  }

  if (isValueEmpty(change.old_value) && !isValueEmpty(change.new_value)) {
    return `set ${label} to ${next}`;
  }

  if (!isValueEmpty(change.old_value) && isValueEmpty(change.new_value)) {
    return `cleared ${label}`;
  }

  if (previous === next) {
    if (label === 'details' || label === 'record') {
      return next !== 'Not set' ? `recorded ${next} for the record` : 'recorded the record details';
    }

    return next !== 'Not set' ? `recorded ${label}: ${next}` : `recorded ${label}`;
  }

  return `changed ${label} from ${previous} to ${next}`;
};

const getEntrySummary = (entry: ActivityEntry, options?: { includeSource?: boolean }): string => {
  const displayChanges = getDisplayChanges(entry.changes);
  const changeCount = displayChanges.length;
  const includeSource = Boolean(options?.includeSource);

  if (entry.action === 'offline_queued') {
    return withSourceContext(
      `saved ${getOfflineTimelineLabel(String(entry.metadata?.offline_action || ''), String(entry.metadata?.timeline_label || ''))} offline`,
      entry,
      includeSource,
    );
  }

  if (entry.action === 'offline_failed') {
    return withSourceContext(
      `could not sync ${getOfflineTimelineLabel(String(entry.metadata?.offline_action || ''), String(entry.metadata?.timeline_label || ''))}`,
      entry,
      includeSource,
    );
  }

  let summary: string;

  switch (entry.action) {
    case 'created':
      summary = 'created this item';
      break;
    case 'deleted':
      summary = 'deleted this item';
      break;
    case 'work_item_visible': {
      const itemReference = getEntryItemReference(entry);
      const reason = typeof entry.metadata?.visibility_reason === 'string'
        ? entry.metadata.visibility_reason.trim()
        : '';
      const ref = itemReference || 'this record';
      if (reason === 'list_refresh') {
        summary = `refreshed their SmartTask queue — ${ref} surfaced`;
      } else if (reason) {
        summary = `${ref} surfaced in SmartTask (${reason.replace(/[_-]+/g, ' ')})`;
      } else {
        summary = `made ${ref} available in SmartTask`;
      }
      break;
    }
    case 'work_item_opened': {
      const itemReference = getEntryItemReference(entry);
      summary = itemReference
        ? `opened ${itemReference} in SmartTask`
        : 'opened this record in SmartTask';
      break;
    }
    case 'ui_action':
      summary = getUiActionSummary(entry);
      break;
    case 'open':
    case 'opened':
      summary = 'opened this record';
      break;
    case 'viewed':
      summary = 'viewed this record';
      break;
    case 'assigned':
      summary = changeCount === 1 ? getChangeSummary(displayChanges[0]) : 'assigned this item';
      break;
    case 'unassigned':
      summary = 'removed the assignee';
      break;
    case 'reassigned':
      summary = changeCount === 1 ? getChangeSummary(displayChanges[0]) : 'reassigned this item';
      break;
    case 'field_updated':
      summary = changeCount === 1
        ? getChangeSummary(displayChanges[0])
        : `updated ${changeCount} details`;
      break;
    case 'updated':
      summary = changeCount === 1
        ? getChangeSummary(displayChanges[0])
        : changeCount > 1
          ? `updated ${changeCount} details`
          : 'updated this item';
      break;
    case 'comment_added':
    case 'commented': {
      const metaCt = entry.metadata && typeof entry.metadata === 'object' && entry.metadata !== null
        && 'comment_type' in entry.metadata
        ? String((entry.metadata as { comment_type?: unknown }).comment_type ?? '')
        : '';
      summary = metaCt === 'billing' ? 'added a billing comment' : 'added a comment';
      break;
    }
    case 'comment_updated':
      summary = 'edited a comment';
      break;
    case 'comment_deleted':
      summary = 'deleted a comment';
      break;
    case 'status_changed':
      summary = changeCount === 1 ? getChangeSummary(displayChanges[0]) : 'changed the status';
      break;
    case 'status_cascaded':
      summary = 'updated a linked record automatically';
      break;
    case 'automation_executed':
      summary = 'ran an automated update';
      break;
    case 'completion_submitted':
      summary = 'submitted completion for approval';
      break;
    case 'workflow_transition':
      summary = changeCount === 1 ? getChangeSummary(displayChanges[0]) : 'updated the SmartTask progress';
      break;
    case 'time_entry_edited':
      summary = changeCount === 1
        ? `edited a timer: ${getChangeSummary(displayChanges[0])}`
        : changeCount > 1
          ? `edited a timer (${changeCount} changes)`
          : 'edited a timer';
      break;
    case 'related_time_entry_edited':
      summary = changeCount === 1
        ? `edited a related timer: ${getChangeSummary(displayChanges[0])}`
        : changeCount > 1
          ? `edited a related timer (${changeCount} changes)`
          : 'edited a related timer';
      break;
    case 'time_entry_deleted':
      summary = 'deleted a timer';
      break;
    case 'related_time_entry_deleted':
      summary = 'deleted a related timer';
      break;
    case 'subtask_added': {
      const subtaskNumber = entry.metadata?.subtask_number;
      const subtaskTitle = entry.metadata?.subtask_title;
      summary = subtaskNumber && subtaskTitle
        ? `added subtask ${subtaskNumber}: ${subtaskTitle}`
        : 'added a subtask';
      break;
    }
    case 'subtask_status_changed': {
      const subtaskNumber = entry.metadata?.subtask_number;
      const subtaskTitle = entry.metadata?.subtask_title;
      const fromStatus = entry.metadata?.from_status;
      const toStatus = entry.metadata?.to_status;
      const subtaskRef = subtaskNumber && subtaskTitle
        ? `${subtaskNumber}: ${subtaskTitle}`
        : 'a subtask';
      if (fromStatus && toStatus) {
        summary = `changed ${subtaskRef} status from ${fromStatus} to ${toStatus}`;
      } else {
        summary = `updated ${subtaskRef}`;
      }
      break;
    }
    case 'subtask_removed': {
      const subtaskNumber = entry.metadata?.subtask_number;
      const subtaskTitle = entry.metadata?.subtask_title;
      summary = subtaskNumber && subtaskTitle
        ? `removed subtask ${subtaskNumber}: ${subtaskTitle}`
        : 'removed a subtask';
      break;
    }
    case 'photo_captured': {
      const photoType = typeof entry.metadata?.photo_type === 'string' ? entry.metadata.photo_type : '';
      const caption = typeof entry.metadata?.caption === 'string' ? entry.metadata.caption.trim() : '';
      const typeLabel = photoType ? ` (${photoType})` : '';
      summary = caption
        ? `captured a photo${typeLabel}: ${caption}`
        : `captured a photo${typeLabel}`;
      break;
    }
    case 'photo_updated':
      summary = changeCount === 1 ? getChangeSummary(displayChanges[0]) : 'updated a photo';
      break;
    case 'photo_deleted':
      summary = 'deleted a photo';
      break;
    case 'photo_converted_to_webp':
      summary = 'optimized a photo for faster loading';
      break;
    case 'signature_captured':
      summary = 'captured a signature';
      break;
    case 'signature_requested':
      summary = 'requested a signature';
      break;
    case 'assignee_added': {
      const assigneeName = typeof entry.metadata?.assignee_name === 'string' ? entry.metadata.assignee_name : '';
      summary = assigneeName ? `added ${assigneeName} as an assignee` : 'added an assignee';
      break;
    }
    case 'assignee_removed': {
      const assigneeName = typeof entry.metadata?.assignee_name === 'string' ? entry.metadata.assignee_name : '';
      summary = assigneeName ? `removed ${assigneeName} as an assignee` : 'removed an assignee';
      break;
    }
    case 'time_logged':
      summary = changeCount === 1 ? getChangeSummary(displayChanges[0]) : 'logged time';
      break;
    case 'public_view':
      summary = 'viewed via a public link';
      break;
    case 'save':
      summary = changeCount === 1 ? getChangeSummary(displayChanges[0]) : 'saved changes';
      break;
    default:
      summary = `recorded ${toSentenceLabel(humanizeTechnicalPhrase(entry.action))}`;
      break;
  }

  return withSourceContext(summary, entry, includeSource);
};

const getEntryNarrative = (entry: ActivityEntry): string | null => {
  const displayChanges = getDisplayChanges(entry.changes);

  if (entry.action === 'offline_queued') {
    return 'This Smart Task action is saved in encrypted offline storage and will sync when connectivity returns.';
  }

  if (entry.action === 'offline_failed') {
    const errorMessage = typeof entry.metadata?.offline_error === 'string' && entry.metadata.offline_error.trim().length > 0
      ? entry.metadata.offline_error.trim()
      : null;
    return errorMessage
      ? `This queued Smart Task action could not sync yet: ${errorMessage}`
      : 'This queued Smart Task action could not sync yet.';
  }

  if (displayChanges.length === 0) {
    if (entry.action === 'comment_added' || entry.action === 'commented') {
      return 'A comment was added to this record.';
    }
    if (entry.action === 'comment_updated') {
      return 'A comment on this record was updated.';
    }
    if (entry.action === 'comment_deleted') {
      return 'A comment was removed from this record.';
    }
    if (entry.action === 'created') {
      return 'This record was created.';
    }
    if (entry.action === 'work_item_visible') {
      const reason = typeof entry.metadata?.visibility_reason === 'string'
        ? entry.metadata.visibility_reason.trim()
        : '';
      if (reason === 'list_refresh') {
        return 'This record appeared in the SmartTask queue when the list was refreshed — it is not a status change.';
      }
      return 'This record became visible in SmartTask.';
    }
    if (entry.action === 'work_item_opened') {
      return 'This record was opened in SmartTask.';
    }
    if (entry.action === 'ui_action') {
      return 'An in-app action was recorded.';
    }
    if (entry.action === 'open' || entry.action === 'opened' || entry.action === 'viewed') {
      return 'This record was viewed.';
    }
    if (entry.action === 'photo_captured') {
      return 'A photo was captured and attached to this record.';
    }
    if (entry.action === 'signature_captured') {
      return 'A signature was captured for this record.';
    }
    if (entry.action === 'status_cascaded') {
      return 'This record was updated automatically to stay aligned with a linked record.';
    }
    if (entry.action === 'automation_executed') {
      return 'An automated rule updated this record.';
    }
    if (entry.action === 'workflow_transition') {
      return 'The SmartTask progress for this record was updated.';
    }
    return null;
  }

  if (displayChanges.length === 1) {
    return getChangeNarrative(displayChanges[0]);
  }

  const labels = Array.from(
    new Set(
      displayChanges
        .map((change) => toSentenceLabel(formatFieldName(change.field)))
        .filter(Boolean)
    )
  );

  if (labels.length <= 3) {
    return `This update included changes to ${formatList(labels)}.`;
  }

  return `This update included changes to ${formatList(labels.slice(0, 3))}, and ${labels.length - 3} other details.`;
};

const formatActionLabel = (action: string): string => {
  switch (action) {
    case 'offline_queued':
      return 'Saved offline';
    case 'offline_failed':
      return 'Sync failed';
    case 'work_item_visible':
      return 'Queue refresh';
    case 'work_item_opened':
      return 'Opened in SmartTask';
    case 'ui_action':
      return 'App activity';
    case 'open':
    case 'opened':
    case 'viewed':
      return 'Viewed';
    case 'photo_captured':
      return 'Photo added';
    case 'photo_updated':
      return 'Photo updated';
    case 'photo_deleted':
      return 'Photo removed';
    case 'photo_converted_to_webp':
      return 'Photo optimized';
    case 'signature_captured':
      return 'Signature captured';
    case 'signature_requested':
      return 'Signature requested';
    case 'assignee_added':
      return 'Assignee added';
    case 'assignee_removed':
      return 'Assignee removed';
    case 'time_logged':
      return 'Time logged';
    case 'public_view':
      return 'Public view';
    case 'field_updated':
      return 'Update';
    case 'comment_added':
    case 'commented':
      return 'Comment added';
    case 'comment_updated':
      return 'Comment edited';
    case 'comment_deleted':
      return 'Comment removed';
    case 'status_changed':
      return 'Status change';
    case 'status_cascaded':
      return 'Automatic update';
    case 'automation_executed':
      return 'Automation';
    case 'completion_submitted':
      return 'Completion submitted';
    case 'workflow_transition':
      return 'SmartTask update';
    case 'time_entry_edited':
      return 'Timer edited';
    case 'related_time_entry_edited':
      return 'Related timer edited';
    case 'time_entry_deleted':
      return 'Timer deleted';
    case 'related_time_entry_deleted':
      return 'Related timer deleted';
    case 'subtask_added':
      return 'Subtask added';
    case 'subtask_status_changed':
      return 'Subtask updated';
    case 'subtask_removed':
      return 'Subtask removed';
    case 'created':
      return 'Created';
    case 'deleted':
      return 'Deleted';
    default:
      return toTitleCase(action);
  }
};

const getActionGroup = (entry: ActivityEntry): 'changes' | 'comments' | 'status' | 'system' => {
  if (entry.action.includes('comment')) return 'comments';
  if (
    entry.action === 'workflow_transition' ||
    entry.action === 'status_changed' ||
    entry.changes?.some((change) => change.field === 'status') ||
    entry.field_changed === 'status'
  ) {
    return 'status';
  }
  if (
    entry.action === 'field_updated' ||
    entry.action === 'updated' ||
    (entry.changes && entry.changes.length > 0)
  ) {
    return 'changes';
  }
  return 'system';
};

const getUniqueTabLabels = (entry: ActivityEntry): string[] => {
  const labels = new Set<string>();
  const displayChanges = getDisplayChanges(entry.changes);

  if (displayChanges.length) {
    displayChanges.forEach((change) => labels.add(getTabLabelForField(change.field)));
  } else if (entry.field_changed) {
    labels.add(getTabLabelForField(entry.field_changed));
  } else if (entry.action.includes('comment')) {
    labels.add('Comments');
  }

  if (labels.size === 0) {
    labels.add('Activity');
  }

  return Array.from(labels);
};

function getChangeNarrative(change: ActivityChange): string {
  return `${toSentenceCase(getChangeSummary(change))}.`;
}

const buildActivitySearchText = (entry: ActivityEntry, includeSource: boolean): string => {
  const displayChanges = getDisplayChanges(entry.changes);

  return [
    entry.action,
    entry.user?.name,
    entry.user?.email,
    getEntrySummary(entry, { includeSource }),
    getEntryNarrative(entry),
    entry.metadata?.comment_text,
    entry.metadata?.deleted_comment_text,
    entry.metadata?.override_reason,
    entry.metadata?.automation_name,
    entry.metadata?.source_work_item_number,
    entry.metadata?.source_work_item_type,
    entry.metadata?.trigger_source,
    ...(entry.tags || []),
    ...displayChanges.flatMap((change) => [
      formatFieldName(change.field),
      getChangePreviousValue(change),
      getChangeNextValue(change),
      getChangeNarrative(change),
    ]),
  ]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .join(' ')
    .toLowerCase();
};

const renderSummaryCard = (label: string, value: string, isDark: boolean) => (
  <div
    className={`rounded-lg border px-2.5 py-2 ${isDark ? 'border-gray-700 bg-gray-900/70 text-gray-100' : 'border-gray-200 bg-gray-50 text-gray-900'}`}
  >
    <div className={`text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
      {label}
    </div>
    <div className="mt-1 text-sm leading-5 break-words">{value}</div>
  </div>
);

const renderSnapshotRows = (rows: PrettyValueRow[], isDark: boolean) => (
  <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-gray-700 bg-gray-950/50' : 'border-gray-200 bg-white'}`}>
    {rows.map((row, index) => (
      <div
        key={row.key}
        className={`grid gap-3 px-3 py-3 md:grid-cols-[200px_minmax(0,1fr)] ${index !== rows.length - 1 ? (isDark ? 'border-b border-gray-800' : 'border-b border-gray-100') : ''
          }`}
      >
        <div className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          {row.label}
        </div>
        <div className={`text-sm leading-6 break-words ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>{row.value}</div>
      </div>
    ))}
  </div>
);

const renderComparisonRows = (rows: ComparisonRow[], isDark: boolean) => (
  <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-gray-700 bg-gray-950/50' : 'border-gray-200 bg-white'}`}>
    {rows.map((row, index) => (
      <div
        key={row.key}
        className={`grid gap-3 px-3 py-3 md:grid-cols-[180px_minmax(0,1fr)_minmax(0,1fr)] ${index !== rows.length - 1 ? (isDark ? 'border-b border-gray-800' : 'border-b border-gray-100') : ''
          }`}
      >
        <div className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          {row.label}
        </div>
        {renderSummaryCard('Previous', row.previous, isDark)}
        {renderSummaryCard('Updated', row.next, isDark)}
      </div>
    ))}
  </div>
);

const renderChangeDetails = (change: ActivityChange, isDark: boolean) => {
  const comparisonRows = buildComparisonRows(change);
  const changedRows = comparisonRows.filter((row) => row.changed);
  const hasStructuredValue =
    !hasResolvedDisplayValue(change) && (
      isPlainObject(change.old_value) ||
      isPlainObject(change.new_value) ||
      Array.isArray(change.old_value) ||
      Array.isArray(change.new_value)
    );

  if (hasStructuredValue && changedRows.length > 0) {
    return renderComparisonRows(changedRows, isDark);
  }

  if (hasStructuredValue) {
    const snapshotRows = flattenValueRows(change.new_value, change.field || 'value');
    return renderSnapshotRows(snapshotRows.length > 0 ? snapshotRows : flattenValueRows(change.old_value, change.field || 'value'), isDark);
  }

  if (comparisonRows.length === 1 && !comparisonRows[0].changed) {
    return renderSummaryCard('Recorded value', comparisonRows[0].next, isDark);
  }

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {renderSummaryCard('Previous', comparisonRows[0]?.previous || 'Not set', isDark)}
      {renderSummaryCard('Updated', comparisonRows[0]?.next || 'Not set', isDark)}
    </div>
  );
};

export default function ActivityTimeline({
  workItemId,
  isActive,
  includeRelated = false,
  groupId = null,
  onActivityCountChange,
}: ActivityTimelineProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const currentUser = authService.getUser() as CurrentUserSnapshot | null;
  const currentClientId = currentUser?.client_id ? Number(currentUser.client_id) : 0;
  const currentUserIdentity = useMemo<ActivityUser>(() => ({
    id: currentUser?.id ? Number(currentUser.id) : undefined,
    email: typeof currentUser?.email === 'string' ? currentUser.email : undefined,
    name: typeof currentUser?.name === 'string' && currentUser.name.trim().length > 0
      ? currentUser.name
      : [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ').trim() || undefined,
  }), [currentUser?.email, currentUser?.first_name, currentUser?.id, currentUser?.last_name, currentUser?.name]);

  const [activities, setActivities] = useState<ActivityEntry[]>([]);
  const [offlineActivities, setOfflineActivities] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  // Background refresh (websocket / autosave events). Unlike `loading`, this never
  // unmounts the list, so the user's scroll position is preserved while data swaps in.
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalActivities, setTotalActivities] = useState(0);
  const [filter, setFilter] = useState<'all' | 'changes' | 'comments' | 'status' | 'system'>('all');
  // Default: hide UI noise (component mounts, list refreshes) and backend system events
  // that flood the timeline with zero business value. Users can toggle to see everything.
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [expandedEntries, setExpandedEntries] = useState<Record<string, boolean>>({});

  const hasLoadedRef = useRef(false);
  const realtimeRefreshTimeoutRef = useRef<number | null>(null);
  // Tracks the deepest page the user has loaded so background refreshes can re-fetch
  // every loaded page without truncating the list (preserves scroll position).
  const loadedPageRef = useRef(1);

  useEffect(() => {
    hasLoadedRef.current = false;
    loadedPageRef.current = 1;
    setActivities([]);
    setOfflineActivities([]);
    setPage(1);
    setHasMore(true);
    setTotalActivities(0);
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setExpandedEntries({});
  }, [groupId, workItemId]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 250);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [searchQuery]);

  const loadOfflineActivity = useCallback(async () => {
    if (!currentClientId || !currentUserIdentity.id) {
      setOfflineActivities([]);
      return;
    }

    try {
      const [pendingQueue, history] = await Promise.all([
        masterSync.getPendingSmartTaskActions({
          clientId: currentClientId,
          userId: currentUserIdentity.id,
        }),
        masterSync.getSmartTaskOfflineActionHistory({
          clientId: currentClientId,
          userId: currentUserIdentity.id,
          limit: 25,
        }),
      ]);

      const scope = {
        workItemId: String(workItemId),
        includeRelated,
        groupId,
      };

      const queuedEntries = pendingQueue
        .filter((entry) => matchesOfflineScope(entry, scope))
        .map((entry) => buildOfflineActivityEntry(entry, 'queued', currentUserIdentity));

      const failedEntries = history
        .filter((entry) => entry.status === 'failed')
        .filter((entry) => matchesOfflineScope(entry, scope))
        .map((entry) => buildOfflineActivityEntry(entry, 'failed', currentUserIdentity));

      setOfflineActivities(
        [...queuedEntries, ...failedEntries]
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      );
    } catch (error) {
      console.warn('[ActivityTimeline] Failed to load offline SmartTask activity:', error);
      setOfflineActivities([]);
    }
  }, [currentClientId, currentUserIdentity, groupId, includeRelated, workItemId]);

  const loadActivity = useCallback(async (pageNumber = 1, append = false, options?: { background?: boolean }) => {
    try {
      if (pageNumber === 1) {
        // A background refresh (websocket / autosave event) must never swap `loading`
        // to true, because the `loading` guard unmounts the whole list and resets the
        // user's scroll position. Use `refreshing` instead so the list stays mounted.
        if (options?.background) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }
      } else {
        setLoadingMore(true);
      }

      const response = await fetch(
        `/api/v1/work-items/${workItemId}/activity?page=${pageNumber}&limit=${ITEMS_PER_PAGE}${includeRelated ? '&include_related=true' : ''}${debouncedSearchQuery ? `&search=${encodeURIComponent(debouncedSearchQuery)}` : ''}`,
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load activity');
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to load activity');
      }

      const newEntries = (data.data || []) as ActivityEntry[];
      const total = Number(data.summary?.total_activities ?? data.pagination?.total ?? newEntries.length ?? 0);

      setActivities((prev) => (append ? [...prev, ...newEntries] : newEntries));
      setHasMore(Boolean(data.pagination?.has_more));
      setPage(pageNumber);
      loadedPageRef.current = Math.max(loadedPageRef.current, pageNumber);
      setTotalActivities(total);

      setExpandedEntries((prev) => {
        const next = { ...prev };
        newEntries.forEach((entry) => {
          if (next[entry.id] === undefined) {
            next[entry.id] = false;
          }
        });
        return next;
      });
    } catch (error) {
      console.error('Error loading activity:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  }, [debouncedSearchQuery, includeRelated, workItemId]);

  // Background refresh that re-fetches every page the user has already loaded, so a
  // realtime/autosave event updates the data in place without unmounting the list or
  // truncating paginated content (which would snap scroll back to the top).
  const refreshLoadedPages = useCallback(async () => {
    try {
      setRefreshing(true);
      const targetPages = Math.max(1, loadedPageRef.current);
      let accumulated: ActivityEntry[] = [];
      let lastHasMore = true;
      let total = 0;

      for (let p = 1; p <= targetPages; p++) {
        const response = await fetch(
          `/api/v1/work-items/${workItemId}/activity?page=${p}&limit=${ITEMS_PER_PAGE}${includeRelated ? '&include_related=true' : ''}${debouncedSearchQuery ? `&search=${encodeURIComponent(debouncedSearchQuery)}` : ''}`,
          {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to load activity');
        }

        const data = await response.json();
        if (!data.success) {
          throw new Error(data.error || 'Failed to load activity');
        }

        const newEntries = (data.data || []) as ActivityEntry[];
        accumulated = [...accumulated, ...newEntries];
        lastHasMore = Boolean(data.pagination?.has_more);
        total = Number(data.summary?.total_activities ?? data.pagination?.total ?? accumulated.length ?? 0);
        if (!lastHasMore) break;
      }

      setActivities(accumulated);
      setHasMore(lastHasMore);
      setTotalActivities(total);

      setExpandedEntries((prev) => {
        const next = { ...prev };
        accumulated.forEach((entry) => {
          if (next[entry.id] === undefined) {
            next[entry.id] = false;
          }
        });
        return next;
      });
    } catch (error) {
      console.error('Error refreshing activity:', error);
    } finally {
      setRefreshing(false);
    }
  }, [debouncedSearchQuery, includeRelated, workItemId]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      if (workItemId?.startsWith?.('stakeholder-') || parseStakeholderEntityWorkItemId(workItemId)) {
        setLoading(false);
        hasLoadedRef.current = true;
        return;
      }

      void Promise.all([
        loadActivity(1, false),
        loadOfflineActivity(),
      ]);
      hasLoadedRef.current = true;
    }
  }, [isActive, loadActivity, loadOfflineActivity, workItemId]);

  useEffect(() => {
    if (!isActive || !hasLoadedRef.current) {
      return;
    }

    // A new search resets pagination back to page 1.
    loadedPageRef.current = 1;
    void Promise.all([
      loadActivity(1, false),
      loadOfflineActivity(),
    ]);
  }, [debouncedSearchQuery, isActive, loadActivity, loadOfflineActivity]);

  const scheduleRealtimeRefresh = useCallback(() => {
    if (!isActive) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    if (realtimeRefreshTimeoutRef.current) {
      window.clearTimeout(realtimeRefreshTimeoutRef.current);
    }

    realtimeRefreshTimeoutRef.current = window.setTimeout(() => {
      realtimeRefreshTimeoutRef.current = null;
      void Promise.all([
        refreshLoadedPages(),
        loadOfflineActivity(),
      ]);
    }, 120);
  }, [isActive, loadOfflineActivity, refreshLoadedPages]);

  const isRelevantRealtimeMessage = useCallback((message: RealtimeMessage) => {
    const scopedWorkItemId = String(workItemId);
    const messageGroupId = typeof message.group_id === 'string' ? message.group_id : null;
    const messageWorkItemId = message.work_item_id != null ? String(message.work_item_id) : null;
    const messageParentJobId = message.parent_job_id != null ? String(message.parent_job_id) : null;
    const messageAppointmentId = message.appointment_id != null ? String(message.appointment_id) : null;
    const messageAppointmentIds = Array.isArray(message.appointment_ids)
      ? message.appointment_ids.map((value) => String(value))
      : [];

    if (groupId && messageGroupId === groupId) {
      return true;
    }

    if (
      messageWorkItemId === scopedWorkItemId
      || messageAppointmentId === scopedWorkItemId
      || messageAppointmentIds.includes(scopedWorkItemId)
    ) {
      return true;
    }

    if (includeRelated && messageParentJobId === scopedWorkItemId) {
      return true;
    }

    return false;
  }, [groupId, includeRelated, workItemId]);

  useWebSocketChannel({
    channel: currentClientId ? `operations:client:${currentClientId}` : undefined,
    autoConnect: true,
    onMessage: (data) => {
      const message = (data || {}) as RealtimeMessage;
      const type = typeof message.type === 'string' ? message.type : '';
      if (!type) {
        return;
      }

      if (
        (
          type === 'operations_refresh'
          || type === 'appointment_status_changed'
          || type === 'gpa_lifecycle_changed'
          || type.startsWith('work_item_')
        )
        && isRelevantRealtimeMessage(message)
      ) {
        scheduleRealtimeRefresh();
      }
    },
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener(OPERATIONS_SYNC_PENDING_CHANGED_EVENT, scheduleRealtimeRefresh);
    window.addEventListener('smarttask-sync-complete', scheduleRealtimeRefresh as EventListener);
    window.addEventListener('workItemStatusChange', scheduleRealtimeRefresh as EventListener);
    window.addEventListener('workItemTimeEntriesChanged', scheduleRealtimeRefresh as EventListener);
    window.addEventListener(WORK_ITEM_LIST_CHANGED_EVENT, scheduleRealtimeRefresh as EventListener);

    return () => {
      if (realtimeRefreshTimeoutRef.current) {
        window.clearTimeout(realtimeRefreshTimeoutRef.current);
        realtimeRefreshTimeoutRef.current = null;
      }

      window.removeEventListener(OPERATIONS_SYNC_PENDING_CHANGED_EVENT, scheduleRealtimeRefresh);
      window.removeEventListener('smarttask-sync-complete', scheduleRealtimeRefresh as EventListener);
      window.removeEventListener('workItemStatusChange', scheduleRealtimeRefresh as EventListener);
      window.removeEventListener('workItemTimeEntriesChanged', scheduleRealtimeRefresh as EventListener);
      window.removeEventListener(WORK_ITEM_LIST_CHANGED_EVENT, scheduleRealtimeRefresh as EventListener);
    };
  }, [scheduleRealtimeRefresh]);

  const allActivities = useMemo(() => {
    return [...offlineActivities, ...activities]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [activities, offlineActivities]);

  // Map every user that appears in the timeline to a display name, so shared
  // workflow transitions can resolve `actor_user_id` (the person who triggered a
  // bulk check-in / pause / resume for all assignees) to a real name instead of
  // the opaque "User 277" fallback. Built from the entries themselves — no extra
  // API call required.
  const userIdNameMap = useMemo(() => {
    const map = new Map<number, string>();
    allActivities.forEach((entry) => {
      if (entry.user?.id != null && typeof entry.user.name === 'string' && entry.user.name.trim().length > 0) {
        map.set(Number(entry.user.id), entry.user.name.trim());
      }
    });
    return map;
  }, [allActivities]);

  const resolveActorName = useCallback((actorId: unknown, actorName?: unknown): string | null => {
    if (typeof actorName === 'string' && actorName.trim().length > 0) {
      return actorName.trim();
    }
    if (actorId == null || actorId === '') return null;
    const id = Number(actorId);
    if (!Number.isFinite(id)) return null;
    return userIdNameMap.get(id) || `User ${id}`;
  }, [userIdNameMap]);

  const effectiveTotalActivities = totalActivities + offlineActivities.length;

  useEffect(() => {
    onActivityCountChange?.(effectiveTotalActivities);
  }, [effectiveTotalActivities, onActivityCountChange]);

  // Apply noise filtering and aggregation to produce the final displayed activities.
  //
  // 1. Always hide backend system events (photo_converted_to_webp) — these are internal
  //    optimizations with no user-facing meaning.
  // 2. When `showAllActivity` is false, also hide UI noise (open, work_item_visible,
  //    work_item_opened) that constitutes 60%+ of entries but has zero business value.
  // 3. Aggregate consecutive identical entries from the same user within a 2-minute window
  //    (e.g. "refreshed SmartTask queue" 5 times in 2 min → 1 entry with ×5 badge).
  const { filteredActivities, hiddenNoiseCount } = useMemo(() => {
    // Step 1: Filter out system-only and (optionally) noise actions
    let noiseCount = 0;
    const afterNoiseFilter = showAllActivity
      ? allActivities.filter((entry) => !SYSTEM_ONLY_ACTIONS.has(entry.action))
      : allActivities.filter((entry) => {
        if (SYSTEM_ONLY_ACTIONS.has(entry.action)) {
          noiseCount += 1;
          return false;
        }
        if (NOISE_ACTIONS.has(entry.action)) {
          noiseCount += 1;
          return false;
        }
        return true;
      });

    // Step 2: Apply the category filter (changes/comments/status/system)
    const scopedActivities = filter === 'all'
      ? afterNoiseFilter
      : afterNoiseFilter.filter((entry) => getActionGroup(entry) === filter);

    // Step 3: Apply search query
    const searchedActivities = debouncedSearchQuery
      ? scopedActivities.filter((entry) =>
        buildActivitySearchText(entry, includeRelated).includes(debouncedSearchQuery.toLowerCase()))
      : scopedActivities;

    // Step 4: Aggregate consecutive identical entries from the same user within the time window.
    // Activities are already sorted by created_at desc.
    const aggregated: ActivityEntry[] = [];
    for (const entry of searchedActivities) {
      const prev = aggregated[aggregated.length - 1];
      if (prev) {
        const sameUser = (prev.user?.id ?? null) === (entry.user?.id ?? null);
        const sameAction = prev.action === entry.action;
        const sameSummary = getEntrySummary(prev, { includeSource: includeRelated }) === getEntrySummary(entry, { includeSource: includeRelated });
        const timeDiff = Math.abs(new Date(prev.created_at).getTime() - new Date(entry.created_at).getTime());
        if (sameUser && sameAction && sameSummary && timeDiff <= TIMELINE_AGGREGATION_WINDOW_MS) {
          // Merge into the previous entry — keep the latest timestamp, increment count
          (prev as ActivityEntry & { _aggregatedCount?: number })._aggregatedCount =
            ((prev as ActivityEntry & { _aggregatedCount?: number })._aggregatedCount || 1) + 1;
          prev.created_at = entry.created_at; // keep the most recent timestamp
          continue;
        }
      }
      aggregated.push({ ...entry });
    }

    return { filteredActivities: aggregated, hiddenNoiseCount: noiseCount };
  }, [allActivities, showAllActivity, filter, debouncedSearchQuery, includeRelated]);

  const groupedActivities = useMemo(() => {
    return filteredActivities.reduce((groups: Record<string, ActivityEntry[]>, entry) => {
      const key = formatDateSAST(entry.created_at, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
      groups[key] = groups[key] || [];
      groups[key].push(entry);
      return groups;
    }, {});
  }, [filteredActivities]);

  const toggleExpanded = (entryId: string) => {
    setExpandedEntries((prev) => ({
      ...prev,
      [entryId]: !prev[entryId],
    }));
  };

  if (loading && activities.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-tour-id="work-item-activity-timeline">
      <div className={`rounded-xl border p-3 ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}>
        <div className="flex flex-col gap-2.5">
          <div className="relative">
            <Search className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search activity, comments, Smart Task events, people, or values"
              className={`w-full rounded-lg border py-2 pl-10 pr-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark
                ? 'border-gray-700 bg-gray-900 text-gray-100 placeholder:text-gray-500'
                : 'border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400'
                }`}
            />
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { key: 'all', label: 'All' },
              { key: 'changes', label: 'Changes' },
              { key: 'comments', label: 'Comments' },
              { key: 'status', label: 'Status' },
              { key: 'system', label: 'System' },
            ].map((option) => (
              <button
                key={option.key}
                onClick={() => setFilter(option.key as typeof filter)}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${filter === option.key
                  ? 'bg-blue-600 text-white'
                  : isDark
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                {option.label}
              </button>
            ))}

            <div className={`ml-auto flex items-center gap-2 text-[11px] font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {hiddenNoiseCount > 0 && !showAllActivity && (
                <span
                  className="px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  }}
                  title="Page views, list refreshes, and system events that are hidden by default"
                >
                  {hiddenNoiseCount} hidden
                </span>
              )}
              <button
                onClick={() => setShowAllActivity(!showAllActivity)}
                className={`px-2 py-0.5 rounded-full transition-colors ${showAllActivity
                  ? 'bg-blue-600 text-white'
                  : isDark
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                title={showAllActivity
                  ? 'Showing all events including page views and list refreshes'
                  : 'Click to show all events including page views and list refreshes'}
              >
                {showAllActivity ? 'All events' : 'Key events'}
              </button>
              <Clock3 className="w-3.5 h-3.5" />
              <span>{debouncedSearchQuery ? `${filteredActivities.length} matching` : `${filteredActivities.length} entries`}</span>
              {refreshing && (
                <Loader2 className="w-3 h-3 animate-spin text-blue-500" />
              )}
            </div>
          </div>
        </div>
      </div>

      {filteredActivities.length === 0 ? (
        <div className={`text-center py-10 rounded-xl border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
          <Activity className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>No matching activity yet</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            {debouncedSearchQuery ? 'Try another search or filter.' : 'Try another filter or make a change to this item.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedActivities).map(([date, entries]) => (
            <div key={date} className={`overflow-hidden rounded-xl border ${isDark ? 'border-gray-800 bg-gray-950/40' : 'border-gray-200 bg-white'}`}>
              <div className={`flex items-center justify-between px-3 py-2 ${isDark ? 'bg-gray-900 text-gray-400 border-b border-gray-800' : 'bg-gray-50 text-gray-500 border-b border-gray-200'}`}>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em]">{date}</div>
                <div className="text-[11px] font-medium">{entries.length} entr{entries.length === 1 ? 'y' : 'ies'}</div>
              </div>

              <div>
                {entries.map((entry) => {
                  const displayChanges = getDisplayChanges(entry.changes);
                  const summary = getEntrySummary(entry, { includeSource: includeRelated });
                  const narrative = displayChanges.length === 0 ? getEntryNarrative(entry) : null;
                  const accentClasses = getActivityAccent(entry, isDark);
                  const uniqueTabs = getUniqueTabLabels(entry);
                  const isExpanded = expandedEntries[entry.id] ?? false;
                  const hasChanges = displayChanges.length > 0;
                  const aggregatedCount = (entry as ActivityEntry & { _aggregatedCount?: number })._aggregatedCount || 1;
                  const changeHighlights = displayChanges.map((change) => getChangeNarrative(change));
                  const rawMetadataComment = entry.metadata?.comment_text || entry.metadata?.deleted_comment_text;
                  const metadataComment = typeof rawMetadataComment === 'string' ? rawMetadataComment : null;
                  const overrideReason = typeof entry.metadata?.override_reason === 'string' && entry.metadata.override_reason.trim().length > 0
                    ? entry.metadata.override_reason.trim()
                    : null;
                  const sourceWorkItemNumber = typeof entry.metadata?.source_work_item_number === 'string'
                    ? entry.metadata.source_work_item_number
                    : null;
                  // GPS plot: workflow_transition entries (field-worker check-in / travel /
                  // pause events) carry the coordinates captured at the moment of the action.
                  // We plot these on a Leaflet map identical to the total-attendance violations
                  // view. Activity-log entries (queue visibility, comments) never carry GPS, so
                  // no map is shown for them — and non-field workers never produce transitions.
                  const entryLocation = getLocationFromEntry(entry);
                  const hasLocationMap = entry.action === 'workflow_transition'
                    && entryLocation != null
                    && isValidCoord(entryLocation);
                  const transitionFromLabel = workflowStateLabel(entry.old_value ?? entry.metadata?.from_state);
                  const transitionToLabel = workflowStateLabel(entry.new_value ?? entry.metadata?.to_state);
                  const applyScope = typeof entry.metadata?.apply_scope === 'string' ? entry.metadata.apply_scope : null;
                  const actorUserIdRaw = entry.metadata?.actor_user_id;
                  const actorUserNameRaw = entry.metadata?.actor_user_name;
                  const hasActor = (typeof actorUserNameRaw === 'string' && actorUserNameRaw.trim().length > 0) || actorUserIdRaw != null;
                  const actorLabel = entry.action === 'workflow_transition' && hasActor
                    ? (() => {
                      const resolved = resolveActorName(actorUserIdRaw, actorUserNameRaw);
                      const toState = String(entry.new_value ?? entry.metadata?.to_state ?? '').trim().toLowerCase();
                      const verb = toState === 'checked_in' || toState === 'working' || toState === 'in_progress'
                        ? 'Started by'
                        : 'Triggered by';
                      return `${verb} ${resolved ?? `User ${actorUserIdRaw}`}`;
                    })()
                    : null;

                  return (
                    <div
                      key={entry.id}
                      className={`border-l-4 px-3 py-2.5 transition-colors hover:${isDark ? 'bg-white/5' : 'bg-gray-50'} ${accentClasses} ${isDark ? 'border-b border-b-gray-800' : 'border-b border-b-gray-100'} last:border-b-0`}
                    >
                      <div className="flex items-start gap-3">
                        <UserAvatar
                          user={{
                            userId: entry.user?.id,
                            displayName: entry.user?.name,
                            email: entry.user?.email,
                          }}
                          size="sm"
                        />

                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-1.5 text-sm leading-5">
                                <UserIdentityLink
                                  user={{
                                    userId: entry.user?.id,
                                    displayName: entry.user?.name,
                                    email: entry.user?.email,
                                  }}
                                  isDark={isDark}
                                  showAvatar={false}
                                  showExternalIcon
                                />
                                <span className={`min-w-0 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                                  {summary}
                                </span>
                                {aggregatedCount > 1 && (
                                  <span
                                    className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${isDark
                                      ? 'bg-white/10 text-white/70'
                                      : 'bg-gray-200 text-gray-500'
                                      }`}
                                    title={`Repeated ${aggregatedCount} times within 2 minutes`}
                                  >
                                    ×{aggregatedCount}
                                  </span>
                                )}
                              </div>

                              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                <span className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                  {formatDateTime(entry.created_at)}
                                </span>
                                <span
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'bg-white/10 text-white/80' : 'bg-white text-gray-600 border border-gray-200'
                                    }`}
                                >
                                  {formatActionLabel(entry.action)}
                                </span>
                                {sourceWorkItemNumber && includeRelated && (
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700'
                                      }`}
                                  >
                                    {sourceWorkItemNumber}
                                  </span>
                                )}
                                {uniqueTabs.map((tabLabel) => (
                                  <span
                                    key={`${entry.id}-${tabLabel}`}
                                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'
                                      }`}
                                  >
                                    {tabLabel}
                                  </span>
                                ))}
                                {(entry.tags || []).map((tag) => (
                                  <span
                                    key={`${entry.id}-${tag}`}
                                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${tag === 'Smart Task'
                                      ? isDark
                                        ? 'bg-violet-900/40 text-violet-300'
                                        : 'bg-violet-100 text-violet-700'
                                      : isDark
                                        ? 'bg-emerald-900/40 text-emerald-300'
                                        : 'bg-emerald-100 text-emerald-700'
                                      }`}
                                  >
                                    {tag}
                                  </span>
                                ))}
                                {entry.action === 'workflow_transition' && applyScope && (
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'
                                      }`}
                                  >
                                    {applyScope === 'all_assignees'
                                      ? 'All assignees'
                                      : applyScope === 'self'
                                        ? 'Only me'
                                        : applyScope}
                                  </span>
                                )}
                                {actorLabel && (
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${isDark ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700'
                                      }`}
                                  >
                                    {actorLabel}
                                  </span>
                                )}
                              </div>

                              {narrative && (
                                <p className={`mt-2 text-sm leading-5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {narrative}
                                </p>
                              )}

                              {!isExpanded && hasChanges && (
                                <div className="mt-2 space-y-1">
                                  {changeHighlights.slice(0, 2).map((highlight, index) => (
                                    <div
                                      key={`${entry.id}-preview-${index}`}
                                      className={`text-xs leading-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                                    >
                                      • {highlight}
                                    </div>
                                  ))}
                                  {changeHighlights.length > 2 && (
                                    <div className={`text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                      +{changeHighlights.length - 2} more change{changeHighlights.length - 2 === 1 ? '' : 's'}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {(hasChanges || metadataComment || overrideReason || hasLocationMap) && (
                              <button
                                onClick={() => toggleExpanded(entry.id)}
                                className={`flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-black/5 text-gray-600'
                                  }`}
                              >
                                {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                {hasLocationMap ? 'Location' : 'Details'}
                              </button>
                            )}
                          </div>

                          {isExpanded && hasLocationMap && entryLocation && (
                            <div className="mt-3 space-y-2.5">
                              <div className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-950/60' : 'border-gray-200 bg-white'}`}>
                                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                  <span className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                                    Location when action occurred
                                  </span>
                                  {transitionFromLabel && transitionToLabel && (
                                    <span className={`text-[11px] font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                      {transitionFromLabel} → {transitionToLabel}
                                    </span>
                                  )}
                                </div>
                                <ViolationMap
                                  latitude={entryLocation.lat}
                                  longitude={entryLocation.lng}
                                  accuracy={entryLocation.accuracy ?? null}
                                  employeeName={entry.user?.name}
                                  violationType={transitionFromLabel && transitionToLabel
                                    ? `${transitionFromLabel} → ${transitionToLabel}`
                                    : 'SmartTask action location'}
                                />
                              </div>
                            </div>
                          )}

                          {isExpanded && hasChanges && !hasLocationMap && (
                            <div className="mt-3 space-y-2.5">
                              {displayChanges.map((change, index) => (
                                <div
                                  key={`${entry.id}-${change.field}-${index}`}
                                  className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-950/60' : 'border-gray-200 bg-white'}`}
                                >
                                  <div className="mb-2 flex flex-wrap items-center gap-1.5">
                                    <span className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                                      {formatFieldName(change.field)}
                                    </span>
                                    <span
                                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'
                                        }`}
                                    >
                                      {getTabLabelForField(change.field)}
                                    </span>
                                    {change.source === 'field_history' && (
                                      <span
                                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${isDark ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                                          }`}
                                      >
                                        Verified change
                                      </span>
                                    )}
                                  </div>

                                  <div className="space-y-3">
                                    {displayChanges.length >= 1 && (
                                      <p className={`text-sm leading-5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {getChangeNarrative(change)}
                                      </p>
                                    )}
                                    {renderChangeDetails(change, isDark)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {isExpanded && metadataComment && !hasChanges && (
                            <div className={`mt-3 rounded-lg border p-3 text-sm whitespace-pre-wrap ${isDark ? 'border-gray-700 bg-gray-950/60 text-gray-200' : 'border-gray-200 bg-white text-gray-700'}`}>
                              {formatSimpleValue(metadataComment)}
                            </div>
                          )}

                          {isExpanded && overrideReason && (
                            <div className={`mt-3 rounded-lg border p-3 ${isDark ? 'border-amber-900/50 bg-amber-950/30 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                              <div className="flex items-start gap-2">
                                <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <div className="text-xs font-semibold uppercase tracking-wide mb-1">Reason for override</div>
                                  <div className="text-sm whitespace-pre-wrap">{overrideReason}</div>
                                </div>
                              </div>
                            </div>
                          )}

                          {!hasChanges && entry.action === 'created' && (
                            <div className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                              <Sparkles className="w-3.5 h-3.5" />
                              Initial record created
                            </div>
                          )}

                          {!hasChanges && entry.action === 'automation_executed' && (
                            <div className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium ${isDark ? 'text-violet-300' : 'text-violet-700'}`}>
                              <RefreshCw className="w-3.5 h-3.5" />
                              {(typeof entry.metadata?.automation_name === 'string' && entry.metadata.automation_name) || 'Automation executed'}
                            </div>
                          )}

                          {!hasChanges && entry.action === 'status_cascaded' && (
                            <div className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                              <Shield className="w-3.5 h-3.5" />
                              Cascaded to child items
                            </div>
                          )}

                          {!hasChanges && metadataComment && !isExpanded && (
                            <div className={`mt-2 text-xs line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {formatSimpleValue(metadataComment)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && filter === 'all' && (
        <div className="text-center pt-2">
          <button
            onClick={() => {
              if (loadingMore || !hasMore) return;
              loadActivity(page + 1, true);
            }}
            disabled={loadingMore}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark
              ? 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700'
              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
          >
            {loadingMore ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading more…
              </span>
            ) : (
              'Load more activity'
            )}
          </button>
        </div>
      )}
    </div>
  );
}
